import { Box, Stack } from '@chakra-ui/react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { noOp } from '@/utils/helpers'

import ShwLink from '@/components/ShwLink'

import type { TabLabel, TabsNormalProps } from './types'

export default function TabsNormal<T extends TabLabel>({
  tabs,
  currentTab,
  onTabChange = noOp,
  tabToUrl,
  hideBorder = false,
  hideTabBorder = false,
  tabToLabel,
  fitTabsToContainerWidth,
  activeTextColor = 'primary.fg',
  defaultTextColor = 'fg.muted',
  buttonProps = {},
  separated = false,
  ...props
}: TabsNormalProps<T>): React.ReactElement {
  return (
    <Box
      overflowX="scroll"
      css={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      <Stack
        spacing={separated ? '8px' : 0}
        direction="row"
        borderBottom={hideBorder ? 'none' : `2px solid ${chakraTokenToVar('border.muted')}`}
        role="tablist"
        maxWidth="100%"
        {...props}
      >
        {tabs.map(tab => {
          const isCurrentTab =
            tab === currentTab || (Array.isArray(currentTab) && currentTab.includes(tab))
          const maybeLabel = tabToLabel?.[tab]
          const label = typeof maybeLabel === 'function' ? maybeLabel() : maybeLabel
          const tabComponent = (
            <Box
              role="tab"
              aria-selected={isCurrentTab}
              cursor="pointer"
              key={`${tab}`}
              onClick={() => {
                onTabChange(tab)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  onTabChange(tab)
                }
              }}
              color={isCurrentTab ? activeTextColor : defaultTextColor}
              fontSize="16px"
              px="16px"
              py={3}
              _active={{ bg: 'bg.muted' }}
              borderBottom={
                hideTabBorder
                  ? 'none'
                  : `2px solid ${isCurrentTab ? chakraTokenToVar('primary.solid') : chakraTokenToVar('border.muted')}`
              }
              fontWeight={500}
              tabIndex={0}
              textAlign="center"
              whiteSpace="nowrap"
              verticalAlign="bottom"
              mb={hideBorder ? '0px' : '-2px'}
              {...buttonProps}
            >
              {label ?? tab}
            </Box>
          )
          const tabUrl = tabToUrl?.(tab)
          return tabUrl == null ? (
            tabComponent
          ) : (
            <ShwLink
              display="inline-block"
              noScroll
              href={tabUrl}
              key={`${tab}`}
              flex={fitTabsToContainerWidth === true ? 1 : undefined}
            >
              {tabComponent}
            </ShwLink>
          )
        })}
      </Stack>
    </Box>
  )
}
